import './index.css'
import { PLAYING } from '@disco/constants/MediaStatus'
import {
  loadNextQueuePage,
  loadPlaybackPaneNextPage,
  pauseTrack,
  playTrack,
} from '@disco/state-management/actions/MediaActions'
import { groupOperations } from '@disco/state-management/ducks/shareable-channels/groups'
import { searchOperations } from '@disco/state-management/ducks/shareable-channels/search'
import { fetchNextPage as fetchSearchNextPage } from '@disco/state-management/reducers/search-page'
import type { TrackStatus } from '@disco/state-management/reducers/media'
import {
  getGroupForPlayback,
  getPlaybackLocation,
  getPlaybackPaneForMediaPlayback,
  getPlaylistForMediaPlayback,
  getPublicChannelSearchResults,
  getQueuePageLoading,
  getSearchPageResultsForPlayback,
  getShortlistForMediaPlayback,
  SEARCH_PAGE_LOCATION,
  selectSearchPageError,
  selectSearchPageIsLoadingNextPage,
  selectTrackPlaybackStatus,
} from '@disco/state-management/selectors/media'
import type { PlaybackLocation } from '@disco/state-management/selectors/media'
import {
  getTrack,
  getTracksByIds,
} from '@disco/state-management/selectors/tracks'
import lodashDebounce from 'lodash/debounce'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import type { ExplorerPane } from 'types/explorer'
import type { GroupId } from 'types/group'
import type { Playlist, PlaylistVersionId } from 'types/playlist'
import type { Shortlist } from 'types/shortlist'
import type { Track, TrackId } from 'types/track'
import type { EventSource } from 'types/ui'
import TrackItem from 'components/common/track-item'
import LoadingMore from 'components/explorer/pane/loading-more'
import { HoverProvider } from 'components/ui/hover-provider'

const mapStateToProps = (
  state,
  {
    trackId,
    trackPlaylistId,
    trackShortlistId,
    mediaSource,
    trackGroupId,
  }: {
    trackId: TrackId
    trackPlaylistId?: PlaylistVersionId
    trackShortlistId?: string
    mediaSource?: EventSource
    trackGroupId?: GroupId
  },
): any => {
  const playbackStatus = selectTrackPlaybackStatus(state, {
    trackId,
  })
  return {
    playbackStatus,
    tracklistItems: getTracksByIds(state, {
      ids: state.media.tracklist,
    }).filter(track => Boolean(track)),
    currentTrack: getTrack(state, {
      id: trackId,
    }),
    trackPlaylist: trackPlaylistId
      ? getPlaylistForMediaPlayback(state, {
          id: trackPlaylistId,
        })
      : null,
    trackShortlist: trackShortlistId
      ? getShortlistForMediaPlayback(state, {
          id: trackShortlistId,
        })
      : null,
    trackPane: getPlaybackPaneForMediaPlayback(state),
    trackGroup:
      mediaSource === 'public-channel'
        ? getGroupForPlayback(state, {
            id: trackGroupId,
          })
        : null,
    publicChannelSearchResults:
      mediaSource === 'public-channel'
        ? getPublicChannelSearchResults(state)
        : null,
    searchPageResults: getSearchPageResultsForPlayback(state),
    searchPageLoading: selectSearchPageIsLoadingNextPage(state),
    searchPageError: selectSearchPageError(state),
    pageLoading: getQueuePageLoading(state),
    tracklist: state.media.tracklist,
    clientLibraryId: state.media.clientLibraryId,
    trackCampaignId: state.media.trackCampaignId,
    trackRecipientId: state.media.trackRecipientId,
    // Get current playback location to preserve it when clicking tracks in queue
    currentPlaybackLocation: getPlaybackLocation(state),
  }
}

const mapDispatchToProps = {
  loadPlaybackPaneNextPage,
  loadNextQueuePage,
  playTrack,
  pauseTrack,
  loadGroupContentsNext: groupOperations.loadGroupContentsNext,
  publicSearchNext: searchOperations.publicSearchNext,
  fetchSearchNextPage,
}
type Props = TrackStatus & {
  open: boolean
  currentTrack: Track
  tracklistItems: Track[]
  trackPlaylist: Playlist | null | undefined
  // TODO: replace with playbackContext
  trackShortlist: Shortlist | null | undefined
  // TODO: replace with playbackContext
  trackPane: ExplorerPane | null | undefined
  trackGroup: Record<string, any> | null | undefined
  publicChannelSearchResults: Record<string, any> | null | undefined
  searchPageResults: { results: Track[]; hasMore: boolean } | null | undefined
  searchPageLoading: boolean
  searchPageError: string | null
  pageLoading: boolean
  playbackStatus: string
  mediaSource: EventSource
  currentPlaybackLocation: PlaybackLocation | null
  // TODO: replace with playbackContext
  clientLibraryId: number | null | undefined
  // TODO: replace with playbackContext
  trackCampaignId: number | null | undefined
  // TODO: replace with playbackContext
  trackRecipientId: number | null | undefined
  // TODO: replace with playbackContext
  tracklist: number[] | null | undefined
  groupId?: GroupId | null | undefined
  playTrack: typeof playTrack
  pauseTrack: typeof pauseTrack
  loadNextQueuePage: typeof loadNextQueuePage
  loadPlaybackPaneNextPage: typeof loadPlaybackPaneNextPage
  loadGroupContentsNext: typeof groupOperations.loadGroupContentsNext
  publicSearchNext: typeof searchOperations.publicSearchNext
  fetchSearchNextPage: typeof fetchSearchNextPage
  trackGroupId: GroupId | null | undefined
}

/**
 * QueuePopup component for displaying the current playback queue
 *
 * A connected component that shows the queue of tracks for the current playback context.
 * Supports different media sources (playlists, shortlists, groups, search results) and
 * handles pagination, track selection, and playback control within the queue.
 *
 * @param props - Component props including Redux state and actions
 * @param props.open - Whether the queue popup is visible
 * @param props.currentTrack - Currently playing track
 * @param props.tracklistItems - Array of tracks in the current tracklist
 * @param props.trackPlaylist - Playlist context for playback
 * @param props.trackShortlist - Shortlist context for playback
 * @param props.trackPane - Explorer pane context for playback
 * @param props.trackGroup - Group context for playback
 * @param props.publicChannelSearchResults - Search results for public channel
 * @param props.searchPageResults - Search page results with tracks and pagination info
 * @param props.searchPageLoading - Whether search page next page is loading
 * @param props.searchPageError - Error message from failed search pagination
 * @param props.pageLoading - Whether next page is loading
 * @param props.playbackStatus - Current playback status
 * @param props.mediaSource - Source of the media being played
 * @param props.currentPlaybackLocation - Current playback location to preserve in queue
 * @param props.playTrack - Action to play a specific track
 * @param props.pauseTrack - Action to pause current track
 * @param props.fetchSearchNextPage - Action to fetch next page of search results
 * @returns Connected JSX element displaying the queue popup
 */
class QueuePopup extends Component<Props> {
  loadNext = lodashDebounce(() => {
    if (this.props.trackPane) {
      const { next } = this.props.trackPane

      if (next) {
        this.props.loadPlaybackPaneNextPage(this.props.trackPane)
      }
    } else if (this.props.searchPageResults) {
      // Block scroll-based fetch when error is set to prevent infinite retry loops.
      // Error is cleared on fetchNextPage.pending, so after manual retry succeeds,
      // scroll-based fetch resumes automatically.
      if (
        this.props.searchPageResults.hasMore &&
        !this.props.searchPageLoading &&
        !this.props.searchPageError
      ) {
        this.props.fetchSearchNextPage()
      }
    } else if (this.props.publicChannelSearchResults) {
      this.props.publicSearchNext()
    } else if (this.props.trackGroup && this.props.trackGroupId) {
      this.props.loadGroupContentsNext(this.props.trackGroupId)
    } else if (this.props.trackPlaylist && !this.props.pageLoading) {
      const { tracks_info } = this.props.trackPlaylist

      if (tracks_info && tracks_info.next) {
        this.props.loadNextQueuePage(tracks_info.next)
      }
    }
  }, 300)
  _handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    e.persist()
    const { scrollHeight, scrollTop, offsetHeight } = e.target as HTMLDivElement

    if (scrollHeight - (scrollTop + offsetHeight) < 1000) {
      this.loadNext()
    }
  }
  _onTrackClick = (track: Track) => {
    const {
      currentTrack,
      trackPlaylist,
      trackShortlist,
      playbackStatus,
      mediaSource,
      trackCampaignId,
      trackRecipientId,
      trackGroupId,
      currentPlaybackLocation,
    } = this.props

    if (currentTrack.id === track.id && playbackStatus === PLAYING) {
      this.props.pauseTrack(track.id)
    } else {
      this.props.playTrack(track.id, mediaSource, {
        playlistId: trackPlaylist ? trackPlaylist.playlist_id : undefined,
        shortlistId: trackShortlist ? trackShortlist.id : undefined,
        campaignId: trackCampaignId,
        tracklist: this.props.tracklist,
        clientLibraryId: this.props.clientLibraryId,
        recipientId: trackRecipientId,
        groupId: trackGroupId,
        queue: true,
        // Preserve current playback location when clicking tracks in queue
        playbackLocation: currentPlaybackLocation,
      })
    }
  }

  render() {
    const {
      currentTrack,
      trackPlaylist,
      trackShortlist,
      trackPane,
      trackGroup,
      publicChannelSearchResults,
      searchPageResults,
      tracklistItems,
    } = this.props
    let loadMore = null
    let tracks = []

    // When playing from search page with a snapshotted tracklist, show the
    // snapshotted tracks instead of live search results. This ensures the queue
    // stays consistent even if the user changes the search query while playing.
    // This check must come BEFORE trackPane because state.explorer.playbackPane
    // may still be set from previous navigation.
    const hasSnapshottedSearchTracklist =
      this.props.currentPlaybackLocation === SEARCH_PAGE_LOCATION &&
      tracklistItems.length > 0

    if (hasSnapshottedSearchTracklist) {
      // Use snapshotted tracklist for search page playback
      tracks = tracklistItems
    } else if (trackPane) {
      tracks = trackPane.results
      if (trackPane.next) loadMore = <LoadingMore />
    } else if (searchPageResults) {
      // Fallback for legacy search playback without snapshotted tracklist
      tracks = searchPageResults.results
      if (this.props.searchPageError) {
        loadMore = (
          <button
            type="button"
            className="Player_QueuePopup_Error Player_QueuePopup_Error--retry"
            onClick={() => this.props.fetchSearchNextPage()}
          >
            Failed to load more results. Tap to retry.
          </button>
        )
      } else if (searchPageResults.hasMore) {
        loadMore = <LoadingMore />
      }
    } else if (publicChannelSearchResults) {
      tracks = publicChannelSearchResults.results
      if (publicChannelSearchResults.next) loadMore = <LoadingMore />
    } else if (tracklistItems.length) {
      tracks = tracklistItems
    } else if (trackGroup) {
      tracks = trackGroup.items
      if (trackGroup.nextPage) loadMore = <LoadingMore />
    } else if (!trackPlaylist && !trackShortlist) {
      tracks = [currentTrack]
    } else if (trackPlaylist && !trackShortlist) {
      tracks = trackPlaylist.tracks

      if (trackPlaylist.tracks_info && trackPlaylist.tracks_info.next) {
        loadMore = <LoadingMore />
      }
    } else if (trackShortlist) {
      tracks = trackShortlist.items.map(item => item.track)
    }

    const trackList = tracks.map((track, index) => {
      const isActive = currentTrack.id === track.id
      const playbackContext = {
        groupId: this.props.trackGroupId,
        sourcePlaylistId: this.props.trackPlaylist?.playlist_id,
        sourceShortlistId: this.props.trackShortlist?.id,
        tracklist: this.props.tracklist,
        queue: true,
        // Preserve playback location when clicking tracks in queue
        playbackLocation: this.props.currentPlaybackLocation,
      }
      return (
        <HoverProvider key={`queue-track-${index}`}>
          <TrackItem
            appearance={isActive ? 'selected' : 'default'}
            location="explorer" // TODO: fix incorrect location="explorer" for /lib/* /playlist-new/*
            track={track}
            onClick={this._onTrackClick.bind(this, track)}
            {...playbackContext}
          />
        </HoverProvider>
      )
    })
    return (
      <div
        className={`Player_QueuePopup ${
          this.props.open
            ? 'Player_QueuePopup--visible'
            : 'Player_QueuePopup--hidden'
        }`}
      >
        <div
          className="Player_QueuePopup_TrackList"
          onScroll={this._handleScroll}
        >
          {trackList}
          {loadMore}
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(QueuePopup) as any
